/**
 * 管理端布局：顶部 Header + 左侧 Sider + 面包屑 + Content。
 *
 * 对齐原型 `prototype/admin/src/layout/AdminLayout.tsx`：
 * - Header：折叠按钮 + 「小象教育管理端」标题 + 通知铃铛 + 岗位 Tag + 员工头像/姓名 Dropdown(含退出)；总部范围用户在标题与铃铛之间常驻全局校区切换器（O-21）
 * - 退出：Modal.confirm 确认对话框（逻辑由 useLogout 封装，含 authApi.logout）
 * - 面包屑：「小象教育 / {分组} / {叶子}」
 * - 左侧菜单：按 GET /auth/permissions 动态渲染（staff-rbac-wechat-login:SC-50）
 * 覆盖 admin-web-shell:SC-04/SC-05/SC-06。
 */
import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { Skeleton } from 'antd';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Avatar, Breadcrumb, Button, Dropdown, Layout, Menu, Space, Tag } from 'antd';
import {
  BankOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  allLeaves,
  isMenuGroup,
  isMenuVisible,
  menuTree,
  type MenuLeaf,
  type MenuNode,
} from './menuConfig';
import { useSession } from '../auth/SessionContext';
import { GlobalCampusScopeProvider, useGlobalCampusScope } from '../auth/GlobalCampusContext';
import { GlobalCampusSwitcher } from './GlobalCampusSwitcher';
import { useLogout } from '../auth/Logout';
import { staffPositionLabel } from '../api/staff.api';
import { startPageRouteSpan } from '../observability/otel';
import { BRAND } from '../theme';

const { Header, Sider, Content } = Layout;

function iconOf(node: { icon?: ComponentType }) {
  return node.icon ? <node.icon /> : undefined;
}

/** 取第一个岗位作为顶栏角色标签兜底（无岗位时显示"员工"） */
function positionTag(positions: string[]): string {
  if (!positions || positions.length === 0) return '员工';
  return staffPositionLabel[positions[0] as keyof typeof staffPositionLabel] ?? positions[0];
}

/** 就绪门控内容区：渲染于 GlobalCampusScopeProvider 内部，消费 scopeReady 决定挂载页面或呈现加载态。 */
function ScopeGateContent() {
  const { scopeReady } = useGlobalCampusScope();
  return (
    <Content>
      {/* 就绪门控：全局校区范围解析完成前不挂载页面（骨架代替）——页面首查即最终口径，
          杜绝「先按 ALL 查询并渲染错误口径、就绪后纠正」的首屏闪烁（O-21 修复） */}
      {scopeReady ? (
        <Outlet />
      ) : (
        <div data-testid="scope-ready-gate" style={{ padding: '48px 0' }}>
          <Skeleton active title={false} paragraph={{ rows: 6 }} />
        </div>
      )}
    </Content>
  );
}

/**
 * 顶栏徽标位就绪门控（O-28 B-01）：总部范围用户在 scopeReady=false（初始挂载解析
 * 持久化值期间）渲染固定尺寸占位，杜绝切换器「先默认值后跳变」的闪现；非总部
 * 范围用户真实渲染本就不含切换器（组件内权限门控），直接放行 children 不产生幻影占位。
 */
function HeaderBadgeGate({
  width,
  testId,
  children,
}: {
  width: number;
  testId: string;
  children: ReactNode;
}) {
  const { permissions } = useSession();
  const { scopeReady } = useGlobalCampusScope();
  const holdForBadge = permissions?.hasAllOrgScope === true && !scopeReady;
  if (!holdForBadge) return <>{children}</>;
  return (
    <div data-testid={testId}>
      <Skeleton.Input active size="small" style={{ width }} />
    </div>
  );
}

export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, permissions, permissionsLoading, refreshPermissions } = useSession();
  const confirmLogout = useLogout();
  const [collapsed, setCollapsed] = useState(false);

  const userPositions = useMemo(() => session?.positions ?? [], [session]);
  const allowedMenuKeys = permissions?.menuKeys;

  const selectedLeaf = useMemo<MenuLeaf | undefined>(() => {
    const sorted = [...allLeaves].sort((a, b) => b.path.length - a.path.length);
    return sorted.find((leaf) =>
      leaf.path === '/' ? location.pathname === '/' : location.pathname.startsWith(leaf.path),
    );
  }, [location.pathname]);

  // 路由切换产生 pageRoute span（携带 pageCode，对齐 ADM 编号），覆盖 admin-web-shell:SC-21
  useEffect(() => {
    if (selectedLeaf?.pageCode) startPageRouteSpan(selectedLeaf.pageCode);
  }, [selectedLeaf?.pageCode]);

  const currentGroup = useMemo(
    () =>
      menuTree.find(
        (node) => isMenuGroup(node) && node.children.some((c) => c.key === selectedLeaf?.key),
      ),
    [selectedLeaf],
  );

  // 菜单动态渲染：按权限过滤可见叶子，空分组整体隐藏（SC-50）
  const menuItems = useMemo(() => {
    const visibleLeaves = (node: MenuNode): MenuLeaf[] =>
      isMenuGroup(node)
        ? node.children.filter((leaf) => isMenuVisible(leaf, userPositions, allowedMenuKeys))
        : isMenuVisible(node, userPositions, allowedMenuKeys)
          ? [node]
          : [];

    return menuTree
      .map((node) =>
        isMenuGroup(node)
          ? {
              key: node.key,
              icon: iconOf(node),
              label: node.label,
              children: visibleLeaves(node).map((leaf) => ({
                key: leaf.key,
                label: <Link to={leaf.path}>{leaf.label}</Link>,
              })),
            }
          : {
              key: node.key,
              icon: iconOf(node),
              label: <Link to={node.path}>{node.label}</Link>,
            },
      )
      // 过滤掉无可见子项的分组
      .filter((item) => !('children' in item) || (item.children?.length ?? 0) > 0);
  }, [userPositions, allowedMenuKeys]);

  return (
    <GlobalCampusScopeProvider>
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          borderBottom: `1px solid var(--admin-color-border)`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Space size={20}>
          {collapsed ? (
            <MenuUnfoldOutlined
              style={{ fontSize: 16, cursor: 'pointer' }}
              onClick={() => setCollapsed(false)}
            />
          ) : (
            <MenuFoldOutlined
              style={{ fontSize: 16, cursor: 'pointer' }}
              onClick={() => setCollapsed(true)}
            />
          )}
          <Space size={8} style={{ fontWeight: 700, fontSize: 16, color: BRAND }}>
            <BankOutlined />
            小象教育管理端
          </Space>
          {/* 全局校区切换器：总部范围用户常驻（组件内按权限门控渲染），校区口径唯一入口（O-21）；
              scopeReady 就绪前渲染固定占位防闪现（O-28 B-01） */}
          <HeaderBadgeGate width={160} testId="header-badge-skeleton-switcher">
            <GlobalCampusSwitcher />
          </HeaderBadgeGate>
        </Space>
        <Space size={18}>
          <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
          {/* 徽标位就绪门控：总部用户解析持久化校区期间固定占位，就绪后一次性显示（O-28 B-01） */}
          <HeaderBadgeGate width={80} testId="header-badge-skeleton-tag">
            <Tag color="green" style={{ marginInlineEnd: 0 }}>
              {/* 徽标口径（遗留清账 E）：总部范围账号固定显示「总部管理员」——岗位首项可能配置为校区管理员，误导数据范围感知；快照未就绪/非总部维持岗位首项兜底 */}
              {permissions?.hasAllOrgScope === true ? '总部管理员' : positionTag(userPositions)}
            </Tag>
          </HeaderBadgeGate>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: '退出登录',
                  onClick: confirmLogout,
                },
              ],
            }}
          >
            <Space size={6} style={{ cursor: 'pointer' }}>
              <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: BRAND }} />
              {session?.name ?? '未登录'}
            </Space>
          </Dropdown>
        </Space>
      </Header>
      <Layout>
        <Sider
          width={220}
          theme="dark"
          collapsed={collapsed}
          style={{ overflow: 'auto', height: 'calc(100vh - 56px)', position: 'sticky', top: 56 }}
        >
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={selectedLeaf ? [selectedLeaf.key] : []}
            defaultOpenKeys={menuTree.filter(isMenuGroup).map((g) => g.key)}
            items={menuItems}
            onClick={(info) => {
              const leaf = allLeaves.find((l) => l.key === info.key);
              if (leaf) navigate(leaf.path);
            }}
          />
        </Sider>
        <Layout style={{ padding: '20px 24px' }}>
          <Breadcrumb
            style={{ marginBottom: 16 }}
            items={[
              { title: '小象教育' },
              ...(currentGroup ? [{ title: currentGroup.label }] : []),
              ...(selectedLeaf ? [{ title: selectedLeaf.label }] : []),
            ]}
          />
          {/* 权限快照拉取失败：菜单按岗位兜底展示可能不完整，提供显式重试入口 */}
          {session && !permissionsLoading && !permissions && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="权限快照加载失败，菜单与操作权限可能不完整"
              action={
                <Button size="small" type="primary" onClick={() => void refreshPermissions()}>
                  重试
                </Button>
              }
            />
          )}
          <ScopeGateContent />
        </Layout>
      </Layout>
    </Layout>
    </GlobalCampusScopeProvider>
  );
}
